// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import type {VoiceStateSyncPartial} from '@app/features/voice/engine/VoiceStateSyncStateMachine';
import type {Participant, Room} from 'livekit-client';

const logger = new Logger('VoiceMediaEngineBridge');

interface MediaEngineAccess {
	room?: Room;
	syncLocalVoiceStateWithServer?: (partial: VoiceStateSyncPartial) => void;
	upsertParticipant?: (participant: Participant) => void;
}

function getMediaEngine(): MediaEngineAccess | null {
	try {
		if (!('_mediaEngine' in window)) return null;
		return (
			(
				window as {
					_mediaEngine?: MediaEngineAccess;
				}
			)._mediaEngine ?? null
		);
	} catch (error) {
		logger.error('Failed to access media engine store', error);
		return null;
	}
}

export function syncLocalVoiceStateWithServer(partial: VoiceStateSyncPartial): void {
	try {
		const store = getMediaEngine();
		store?.syncLocalVoiceStateWithServer?.(partial);
	} catch (error) {
		logger.error('Failed to sync voice state with server', error);
	}
}

export function getRoomFromMediaEngine(): Room | null {
	try {
		const store = getMediaEngine();
		return store?.room ?? null;
	} catch (error) {
		logger.error('Failed to get room from media engine store', error);
		return null;
	}
}

export function updateLocalParticipantFromRoom(roomOverride?: Room | null): void {
	try {
		const store = getMediaEngine();
		if (!store?.upsertParticipant) return;
		const room = roomOverride ?? store.room ?? null;
		if (!room?.localParticipant) return;
		store.upsertParticipant(room.localParticipant);
	} catch (error) {
		logger.error('Failed to update local participant', error);
	}
}
